import { motion } from "motion/react";
import { whatsappLink } from "@/mocks/llkContent";
import { useEffect, useRef, useState } from "react";

const heroBg = "https://readdy.ai/api/search-image?query=Dark%20abstract%20biotechnology%20agriculture%20background%20with%20subtle%20green%20glowing%20particles%2C%20microscopic%20roots%20and%20soil%20texture%2C%20deep%20forest%20green%20and%20black%20gradient%2C%20premium%20dark%20technology%20aesthetic%20with%20golden%20accent%20lines%2C%20no%20text%2C%20no%20products&width=1920&height=1080&seq=llk-hero-bg&orientation=landscape";

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] },
  },
};

const floatLeftVariants = {
  hidden: { opacity: 0, x: -60, rotate: -5 },
  visible: {
    opacity: 0.9,
    x: 0,
    rotate: 0,
    transition: { duration: 1.2, delay: 0.6, ease: [0.25, 0.1, 0.25, 1] },
  },
};

const floatRightVariants = {
  hidden: { opacity: 0, x: 60, rotate: 5 },
  visible: {
    opacity: 0.9,
    x: 0,
    rotate: 0,
    transition: { duration: 1.2, delay: 0.8, ease: [0.25, 0.1, 0.25, 1] },
  },
};

const badgeVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, delay: 1.0, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export default function HeroSection() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Soluções", href: "#solucao" },
    { label: "Produtos", href: "#produtos" },
    { label: "Fases", href: "#fases" },
    { label: "Contato", href: "#contato" },
  ];

  const scrollTo = (href: string) => {
    const el = document.querySelector(href);
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
    setMobileMenuOpen(false);
  };

  return (
    <section ref={heroRef} className="relative w-full min-h-screen flex flex-col">
      {/* Navigation */}
      <motion.nav
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.1, 0.25, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-sm"
            : "bg-transparent"
        }`}
      >
        <div className="w-full flex items-center justify-between px-4 md:px-8 lg:px-12 py-4 md:py-5">
          <div className="flex items-center gap-3">
            <img
              src="https://storage.readdy-site.link/project_files/08db1482-e90f-40f7-90d7-cbafb5b2411e/2a5433d9-3529-4838-8200-c8b0b3cd71e9_LLK-Consultoria-e-Assessoria-de-Negcios-e-Agronegcios.png?v=23a7466bd496da27209b724fc2467f6f"
              alt="LLK Consultoria"
              className="h-11 sm:h-12 md:h-14 w-auto object-contain"
            />
          </div>

          <div className="hidden md:flex items-center gap-8 lg:gap-12">
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollTo(item.href)}
                className={`text-sm font-medium tracking-wide transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? "text-llk-dark hover:text-llk-gold" : "text-white/90 hover:text-llk-gold"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-llk-dark text-white text-sm font-medium rounded-full hover:bg-llk-gold transition-colors whitespace-nowrap"
            >
              <i className="ri-whatsapp-line text-base" />
              Fale Conosco
            </a>
            <button
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${
                scrolled
                  ? "bg-llk-dark/10"
                  : "bg-white/10 backdrop-blur-sm"
              }`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`ri-${mobileMenuOpen ? "close" : "menu"}-line text-xl ${scrolled ? "text-llk-dark" : "text-white"}`} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 px-4 py-4 shadow-lg"
          >
            {navItems.map((item) => (
              <button
                key={item.href}
                onClick={() => scrollTo(item.href)}
                className="block w-full text-left py-3 text-llk-dark font-medium border-b border-gray-100 last:border-0 cursor-pointer active:text-llk-gold"
              >
                {item.label}
              </button>
            ))}
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 mt-4 w-full py-3 bg-llk-dark text-white font-medium rounded-full active:bg-llk-gold transition-colors"
            >
              <i className="ri-whatsapp-line" />
              Fale no WhatsApp
            </a>
          </motion.div>
        )}
      </motion.nav>

      {/* Hero Content */}
      <div className="relative flex-1 flex items-center justify-center min-h-[100dvh] overflow-hidden rounded-none md:rounded-b-[30px]">
        {/* Background */}
        <div className="absolute inset-0">
          <img
            src={heroBg}
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-llk-dark/70 via-llk-dark/50 to-llk-dark/80" />
        </div>

        {/* Floating particles / organic lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.5 }}
            className="absolute top-1/4 left-1/4 w-48 h-48 sm:w-64 sm:h-64 rounded-full bg-llk-gold/5 blur-3xl animate-float-slow"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.5, delay: 0.7 }}
            className="absolute bottom-1/4 right-1/4 w-36 h-36 sm:w-48 sm:h-48 rounded-full bg-llk-green-soft/5 blur-3xl animate-float-medium"
          />
        </div>

        {/* Products floating - desktop only */}
        <div className="absolute inset-0 pointer-events-none hidden lg:block">
          <motion.img
            variants={floatRightVariants}
            initial="hidden"
            animate="visible"
            src="https://storage.readdy-site.link/project_files/08db1482-e90f-40f7-90d7-cbafb5b2411e/66c43649-e48c-4420-b70d-8f1efdc224a4_Scudo-prime-sem-fundo.png?v=f1a27a1327c08fa7940d7a64f1a43f93"
            alt="Scudo Prime"
            className="absolute top-[18%] right-[12%] w-32 xl:w-40 object-contain drop-shadow-2xl animate-float-slow"
          />
          <motion.img
            variants={floatLeftVariants}
            initial="hidden"
            animate="visible"
            src="https://storage.readdy-site.link/project_files/08db1482-e90f-40f7-90d7-cbafb5b2411e/34c4672c-b1a0-4f51-992a-4efc5c89eaad_Predador-prime-sem-fundo.png?v=7b16aeba2b9c5884c6e2b8f3c22a250f"
            alt="Predador Prime"
            className="absolute bottom-[22%] left-[10%] w-28 xl:w-36 object-contain drop-shadow-2xl animate-float-medium"
          />
        </div>

        {/* Text Content */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="relative z-10 text-center px-4 sm:px-6 md:px-8 max-w-4xl mx-auto pt-28 sm:pt-24 md:pt-0"
        >
          <motion.div
            variants={itemVariants}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm mb-5 sm:mb-6 md:mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-llk-green animate-pulse" />
            <span className="text-white/80 text-[11px] sm:text-xs md:text-sm font-medium tracking-wide uppercase">
              Biotecnologia Agrícola
            </span>
          </motion.div>

          <motion.h1
            variants={itemVariants}
            className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl text-white font-bold leading-tight mb-3 sm:mb-4 md:mb-6"
          >
            Proteção Biológica
            <br />
            <span className="italic font-normal text-llk-gold">para o seu campo</span>
          </motion.h1>

          <motion.p
            variants={itemVariants}
            className="text-white/70 text-sm sm:text-base md:text-lg max-w-xl sm:max-w-2xl mx-auto mb-6 sm:mb-8 md:mb-10 leading-relaxed px-2"
          >
            LLK Bio Solutions: soluções consultivas em bioinsumos para fortalecimento radicular,
            biocontrole de nematoides e ativação da microbiota do solo.
          </motion.p>

          <motion.div
            variants={itemVariants}
            className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4"
          >
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center gap-2 sm:gap-3 px-4 sm:px-6 py-3 sm:py-3.5 bg-white text-llk-dark text-sm sm:text-base font-semibold rounded-full hover:bg-llk-gold hover:text-white transition-all duration-300 animate-pulse-gold whitespace-normal sm:whitespace-nowrap w-full sm:w-auto"
            >
              <i className="ri-whatsapp-line text-lg sm:text-xl" />
              Falar com Especialista
              <i className="ri-arrow-right-line text-base sm:text-lg transition-transform group-hover:translate-x-1" />
            </a>
            <button
              onClick={() => scrollTo("#solucao")}
              className="flex items-center justify-center gap-2 px-4 sm:px-6 py-3 sm:py-3.5 border border-white/30 text-white text-sm sm:text-base font-medium rounded-full hover:bg-white/10 transition-all whitespace-normal sm:whitespace-nowrap cursor-pointer w-full sm:w-auto"
            >
              Conhecer Soluções
            </button>
          </motion.div>

          {/* Badges */}
          <motion.div
            variants={badgeVariants}
            className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 md:gap-5 mt-8 sm:mt-10 md:mt-14"
          >
            {["Bioinsumos", "Manejo Biológico", "Alta Performance"].map((badge) => (
              <div
                key={badge}
                className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full bg-white/5 border border-white/10"
              >
                <i className="ri-check-line text-llk-gold text-xs sm:text-sm" />
                <span className="text-white/60 text-[11px] sm:text-xs md:text-sm font-medium">{badge}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}